// DocumentContext.tsx
import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  Dispatch,
  SetStateAction,
  ReactNode,
} from "react";

type PageDimensions = {
  width: number;
  height: number;
};

export type DocumentContextType = {
  containerRef: HTMLElement | null;
  setContainerRef: Dispatch<SetStateAction<HTMLElement | null>>;
  containerWidth: number | undefined;
  scale: number;
  setScale: Dispatch<SetStateAction<number>>;
  rotate: number;
  setRotate: Dispatch<SetStateAction<number>>;
  highlightedElements: HTMLElement[];
  setHighlightedElements: Dispatch<SetStateAction<HTMLElement[]>>;
  pageDimensions: PageDimensions | null;
  setPageDimensions: Dispatch<SetStateAction<PageDimensions | null>>;
  pageRefs: React.MutableRefObject<{ [key: number]: HTMLDivElement | null }>;
  onResize: ResizeObserverCallback;
  selectedText: string | undefined;
  setSelectedText: Dispatch<SetStateAction<string | undefined>>;
  shouldClearSelection: boolean;
  setShouldClearSelection: Dispatch<SetStateAction<boolean>>;
};

export const DocumentContext = createContext<DocumentContextType | undefined>(
  undefined
);

export const DocumentProvider = ({ children }: { children: ReactNode }) => {
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>();
  const [scale, setScale] = useState(1.0);
  const [rotate, setRotate] = useState(0);
  const [highlightedElements, setHighlightedElements] = useState<HTMLElement[]>(
    []
  );
  const [pageDimensions, setPageDimensions] = useState<PageDimensions | null>(
    null
  );
  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const [selectedText, setSelectedText] = useState<string | undefined>(
    undefined
  );
  const [shouldClearSelection, setShouldClearSelection] = useState<boolean>(
    false
  );

  const onResize = useCallback<ResizeObserverCallback>((entries) => {
    const [entry] = entries;
    if (entry) {
      setContainerWidth(entry.contentRect.width);
    }
  }, []);

  return (
    <DocumentContext
      value={{
        containerRef,
        setContainerRef,
        containerWidth,
        scale,
        setScale,
        rotate,
        setRotate,
        highlightedElements,
        setHighlightedElements,
        pageDimensions,
        setPageDimensions,
        pageRefs,
        onResize,
        selectedText,
        setSelectedText,
        shouldClearSelection,
        setShouldClearSelection,
      }}
    >
      {children}
    </DocumentContext>
  );
};
